import { FC, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import cx from "classnames";

import { FeatureDetail } from "./constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  features: FeatureDetail[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
}

const HealthProblemModal: FC<Props> = ({
  isOpen,
  onClose,
  features,
  selectedIndex,
  onSelectIndex,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = "hidden"; // Prevent background scroll
    } else {
      setTimeout(() => setShouldRender(false), 300); // Wait for transition
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  const handleNext = () => {
    onSelectIndex((selectedIndex + 1) % features.length);
  };

  const handlePrev = () => {
    onSelectIndex((selectedIndex - 1 + features.length) % features.length);
  };

  const feature = features[selectedIndex];
  if (!feature) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className={cx(
          "absolute inset-0 bg-black/60 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />

      {/* Slide Drawer */}
      <div
        className={cx(
          "relative flex h-full w-full max-w-[500px] flex-col bg-[#D9D9D9] transition-transform duration-300 ease-in-out md:max-w-[600px]",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Top Image Section */}
        <div className="relative z-10 h-[250px] w-full shrink-0 md:h-[300px]">
          {/* Using a red gradient as fallback if image isn't provided */}
          {feature.image && (
            <img
              src={feature.image}
              alt={feature.title}
              className="absolute inset-0 h-full w-full object-cover mix-blend-overlay"
            />
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
          >
            <X size={20} />
          </button>

          {/* Navigation Arrows */}
          <div className="absolute bottom-0 left-6 flex -translate-y-1/2 gap-3">
            <button
              onClick={handlePrev}
              className="text-dark flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg transition-transform hover:scale-105"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="text-dark flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg transition-transform hover:scale-105"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        {/* Content Section */}
        <div className="my-8 flex-1 overflow-y-auto px-8 md:my-10 md:px-12">
          <h2 className="text-dark mb-8 text-3xl leading-tight font-medium md:text-4xl">
            {feature.title}
          </h2>

          <div className="flex flex-col gap-8">
            {feature.overview && feature.overview.length > 0 && (
              <div>
                <h3 className="text-dark mb-3 text-[18px] font-medium">
                  Apžvalga
                </h3>
                <div className="text-body flex flex-col gap-4 text-sm leading-relaxed md:text-base">
                  {feature.overview.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </div>
            )}

            {feature.sections &&
              feature.sections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-dark mb-3 text-[18px] font-medium">
                    {section.title}
                  </h3>
                  {section.type === "text" ? (
                    <div className="text-body flex flex-col gap-4 text-sm leading-relaxed md:text-base">
                      {section.content.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  ) : (
                    <ul className="text-body flex list-disc flex-col gap-2 pl-5 text-sm leading-relaxed md:text-base">
                      {section.content.map((item, i) => (
                        <li key={i} className="pl-1">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthProblemModal;
